import { useState } from 'react';
import { formatCurrency, type ItemsReconciliation } from '@peles/shared';
import { useUpdateExpenseItem } from '../../hooks/useExpenses';
import { useDialog } from '../../context/DialogContext';
import { extractApiErrorMessage } from '../../utils/apiError';

const TONE = {
  over: 'text-red-700',
  under: 'text-amber-700',
  match: 'text-green-700',
  none: 'text-gray-600',
} as const;

/** What a need's items come to, said against its budget or its price. */
export function describeReconciliation(r: ItemsReconciliation): string {
  const total = formatCurrency(r.total);
  if (r.kind === 'estimate') {
    if (r.against === null) return `Items estimate ${total}`;
    const budget = formatCurrency(r.against);
    if (r.state === 'over') {
      return `Items estimate ${total} · budget ${budget} — over by ${formatCurrency(r.difference!)}`;
    }
    return `Items estimate ${total} · budget ${budget}`;
  }
  if (r.against === null) return `Itemized ${total}`;
  const price = formatCurrency(r.against);
  if (r.state === 'match') return `Itemized ${total} — matches the price`;
  if (r.state === 'over') {
    return `Itemized ${total} of ${price} — over by ${formatCurrency(r.difference!)}`;
  }
  return `Itemized ${total} of ${price} — ${formatCurrency(-r.difference!)} not itemized`;
}

/**
 * The line under a need's items: the items estimate against the budget while
 * it is sourced, the itemization against the agreed price once booked — with
 * the one action that settles a difference (use the total as the budget, or
 * as the price).
 */
export function ItemsReconciliationNote({
  lineId,
  reconciliation,
}: {
  lineId: string;
  reconciliation: ItemsReconciliation;
}) {
  const update = useUpdateExpenseItem();
  const { confirm } = useDialog();
  const [error, setError] = useState('');
  const r = reconciliation;
  const differs = r.state === 'over' || r.state === 'under';
  // A sourced need with items but no budget can take their total as one.
  const offerBudget = r.kind === 'estimate' && (differs || (r.state === 'none' && r.total > 0));
  const offerPrice = r.kind === 'itemized' && differs;

  async function apply() {
    setError('');
    if (offerPrice) {
      const ok = await confirm({
        title: 'Use the itemized total as the price?',
        message: `The line's price becomes ${formatCurrency(r.total)}. If it has a payment schedule, its last payment takes the difference.`,
        confirmLabel: 'Use as price',
        variant: 'warning',
      });
      if (!ok) return;
    }
    try {
      await update.mutateAsync({
        id: lineId,
        updates: offerPrice ? { price: r.total } : { budgetAmount: r.total },
      });
    } catch (err) {
      setError(extractApiErrorMessage(err, 'The line could not be updated'));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="items-reconciliation">
      <span className={`font-medium ${TONE[r.state]}`}>{describeReconciliation(r)}</span>
      {(offerBudget || offerPrice) && (
        <button
          type="button"
          onClick={() => void apply()}
          disabled={update.isPending}
          className="min-h-11 sm:min-h-0 px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {offerPrice ? 'Use itemized total as price' : 'Use as budget'}
        </button>
      )}
      {error && <span className="text-red-600">{error}</span>}
    </div>
  );
}
